import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Settings,
  FolderOpen,
  Package,
  Database,
  BarChart3,
  Search,
  MapPin,
  Building2,
  LogOut,
  Loader2,
  Shield,
  X,
  Palette,
  MessagesSquare,
  LineChart,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { MANAGER, CONTENT_SECTION_ROLES, CONVERSATIONS, ANALYTICS } from "../utils/roles";

const NAV_MAIN = [
  { path: "/", icon: Home, labelKey: "home", exact: true },
  { path: "/configs", icon: Settings, labelKey: "configurations", anyOf: [MANAGER] },
  { path: "/widget", icon: Palette, labelKey: "widget", anyOf: [MANAGER] },
  { path: "/files", icon: FolderOpen, labelKey: "files", anyOf: CONTENT_SECTION_ROLES, allowDept: true },
  { path: "/conversations", icon: MessagesSquare, labelKey: "conversations", anyOf: [CONVERSATIONS, MANAGER] },
  { path: "/analytics", icon: LineChart, labelKey: "analytics", anyOf: [ANALYTICS, MANAGER] },
  { path: "/products", icon: Package, labelKey: "products", adminOnly: true },
  { path: "/cache", icon: Database, labelKey: "cache", adminOnly: true },
  { path: "/vector-store", icon: BarChart3, labelKey: "vectorStore", adminOnly: true },
  { path: "/similarity-search", icon: Search, labelKey: "similaritySearch", adminOnly: true },
  { path: "/store-locations", icon: MapPin, labelKey: "storeLocations", anyOf: [MANAGER] },
];

const NAV_SUPER = [
  { path: "/tenants", icon: Building2, labelKey: "tenants" },
];

const NAV_LABELS = {
  home: { en: "Home", pt: "Início" },
  configurations: { en: "Configurations", pt: "Configurações" },
  widget: { en: "Widget", pt: "Widget" },
  files: { en: "Files", pt: "Ficheiros" },
  conversations: { en: "Conversations", pt: "Conversas" },
  analytics: { en: "Analytics", pt: "Analytics" },
  products: { en: "Products", pt: "Produtos" },
  cache: { en: "Cache", pt: "Cache" },
  vectorStore: { en: "Vector Store", pt: "Vector Store" },
  similaritySearch: { en: "Similarity Search", pt: "Pesquisa Similar" },
  storeLocations: { en: "Store Locations", pt: "Lojas" },
  tenants: { en: "Tenants", pt: "Tenants" },
};

const NavItem = ({ item, active, label, onNav }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={() => onNav(item.path)}
      className="sidebar-nav-item w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mx-0 text-sm font-medium transition-all duration-150 text-left"
      style={{
        color: active ? "var(--color-text-sidebar-active)" : "var(--color-text-sidebar)",
        backgroundColor: active ? "var(--color-sidebar-active)" : "transparent",
        borderLeft: active ? "2px solid var(--color-mint)" : "2px solid transparent",
        paddingLeft: active ? "10px" : "12px",
      }}
    >
      <Icon size={17} style={{ flexShrink: 0, opacity: active ? 1 : 0.7 }} />
      <span>{label}</span>
    </button>
  );
};

const Sidebar = ({ companyName, isOpen, onClose }) => {
  const { translate, language } = useLanguage();
  const { user, logout, isAdmin, isSuperAdmin, isPlatformOnlySuperAdmin, isLoggingOut, hasAllAccess, isManager, canSeeSection, internalProceduresAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getLabel = (key) => {
    return NAV_LABELS[key]?.[language] || NAV_LABELS[key]?.en || key;
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    if (path === "/files") return location.pathname.startsWith("/files");
    if (path === "/store-locations") return location.pathname.startsWith("/store-locations");
    if (path === "/conversations") return location.pathname.startsWith("/conversations");
    return location.pathname === path;
  };

  const handleNav = (path) => {
    if (path === "/files") {
      // FileManagement redirects bare /files to the first collection the
      // user's section roles allow (Task 7).
      navigate("/files");
    } else if (path === "/store-locations") {
      navigate("/store-locations/list");
    } else {
      navigate(path);
    }
    if (onClose) onClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  const getRoleLabel = () => {
    if (isSuperAdmin()) return translate("superAdministrator") || "Super Admin";
    if (isAdmin()) return translate("administrator") || "Admin";
    if (isManager()) return translate("manager") || "Manager";
    const displayRoles = (user?.roles || []).filter((r) => r !== "user");
    if (displayRoles.length > 0) {
      return displayRoles.map((r) => r.replace(/_/g, " ")).join(", ");
    }
    return translate("user") || "User";
  };

  const brandName = companyName || "AI Agent";
  const userInitials = (user?.name || user?.email || "U")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const canSeeNavItem = (item) => {
    if (item.adminOnly) return hasAllAccess();
    if (item.anyOf) {
      return (
        item.anyOf.some((role) => canSeeSection(role)) ||
        (item.allowDept && internalProceduresAccess === true)
      );
    }
    return true; // Home
  };

  // NAV_SUPER ("Tenants") only shows for a platform-only super admin — once
  // a tenant is entered, the sidebar matches what that tenant's own admin
  // would see, and the entry banner is the only way back to the platform.
  const navItems = isPlatformOnlySuperAdmin()
    ? [{ path: "/", icon: Home, labelKey: "home", exact: true }, ...NAV_SUPER]
    : [...NAV_MAIN.filter(canSeeNavItem)];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={onClose}
          style={{ animation: "fadeIn 150ms ease" }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full z-40 flex flex-col sidebar-scroll overflow-y-auto transition-transform duration-250 ease-in-out lg:translate-x-0 ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{
          width: "256px",
          backgroundColor: "var(--color-sidebar-bg)",
          borderRight: "1px solid var(--color-sidebar-border)",
        }}
      >
        {/* Logo area */}
        <div
          className="flex items-center justify-between px-5 flex-shrink-0"
          style={{ height: "65px", borderBottom: "1px solid var(--color-sidebar-border)" }}
        >
          <button
            onClick={() => handleNav("/")}
            className="flex items-center gap-3 hover:opacity-90 transition-opacity"
          >
            {/* Logo mark */}
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "var(--color-mint)" }}
            >
              <img src="/favicon.svg" alt="Kodly" className="w-5 h-5" style={{ filter: "brightness(0) invert(1)" }} />
            </div>
            <div className="text-left">
              <div className="text-white font-semibold text-sm leading-tight">
                {brandName}
              </div>
              <div className="text-xs leading-tight" style={{ color: "var(--color-mint)" }}>
                Backoffice
              </div>
            </div>
          </button>

          {/* Mobile close */}
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden p-1 rounded-lg transition-colors"
              style={{ color: "var(--color-text-sidebar)" }}
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {/* Main nav */}
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              item={item}
              active={isActive(item.path, item.exact)}
              label={getLabel(item.labelKey)}
              onNav={handleNav}
            />
          ))}


        </nav>

        {/* Bottom user area */}
        <div
          className="flex-shrink-0 px-4 py-4"
          style={{ borderTop: "1px solid var(--color-sidebar-border)" }}
        >
          <div className="flex items-center gap-3 mb-3">
            {/* Avatar */}
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
              style={{ backgroundColor: "var(--color-mint)" }}
            >
              {userInitials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate leading-tight">
                {user?.name || user?.email || "User"}
              </p>
              <div className="flex items-center gap-1 mt-0.5">
                {isSuperAdmin() && <Shield size={10} style={{ color: "var(--color-mint)" }} />}
                <p className="text-xs leading-tight truncate" style={{ color: "var(--color-text-sidebar)" }}>
                  {getRoleLabel()}
                </p>
              </div>
            </div>
          </div>

          {/* Logout button */}
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="hover-logout w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
            style={{
              color: "var(--color-text-sidebar)",
              border: "1px solid var(--color-sidebar-border)",
            }}
          >
            {isLoggingOut ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <LogOut size={15} />
            )}
            <span>
              {isLoggingOut
                ? translate("loggingOut") || "Logging out..."
                : translate("logout") || "Logout"}
            </span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
